import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { Bullet } from './Bullet';

export class UFO implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;
  public radius: number;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private isLarge: boolean;
  private gameWidth: number;
  private gameHeight: number;
  private shootTimer: number = 0;
  private shootInterval: number;
  private target: Vector2 | null = null;
  private directionChangeTimer: number = 0;
  private directionChangeInterval: number = 2.0; // Change direction every 2 seconds

  // UFO properties (from official Atari manual)
  private static readonly LARGE_UFO = {
    radius: 20,
    speed: 60,
    shootInterval: 1.5, // seconds
    points: 200, // Large saucer = 200 points
    accuracy: 0.3 // 30% accuracy (mostly random shots)
  };

  private static readonly SMALL_UFO = {
    radius: 12,
    speed: 100,
    shootInterval: 1.0, // seconds  
    points: 1000, // Small saucer = 1000 points
    accuracy: 0.8 // 80% accuracy (aims at player)
  };

  constructor(
    id: string,
    x: number,
    y: number,
    isLarge: boolean,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gameWidth: number,
    gameHeight: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.isLarge = isLarge;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gameWidth = gameWidth;
    this.gameHeight = gameHeight;

    const props = isLarge ? UFO.LARGE_UFO : UFO.SMALL_UFO;
    this.radius = props.radius;
    this.size = { x: this.radius * 2, y: this.radius * 2 };
    this.shootInterval = props.shootInterval;

    // Initial velocity - move horizontally across screen
    const direction = x < gameWidth / 2 ? 1 : -1; // Move toward center
    this.velocity = { x: direction * props.speed, y: 0 };

    // Random shoot timer offset
    this.shootTimer = Math.random() * this.shootInterval;
  }

  public update(deltaTime: number): void {
    // Update shoot timer
    this.shootTimer += deltaTime;

    // Update direction change timer
    this.directionChangeTimer += deltaTime;
    if (this.directionChangeTimer >= this.directionChangeInterval) {
      this.changeDirection();
      this.directionChangeTimer = 0;
    }

    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;

    // Keep UFO on screen vertically
    const margin = this.radius;
    if (this.position.y < margin) {
      this.position.y = margin;
      this.velocity.y = Math.abs(this.velocity.y);
    } else if (this.position.y > this.gameHeight - margin) {
      this.position.y = this.gameHeight - margin;
      this.velocity.y = -Math.abs(this.velocity.y);
    }

    // Remove UFO if it goes off screen horizontally
    if (this.position.x < -this.radius * 2 || this.position.x > this.gameWidth + this.radius * 2) {
      this.active = false;
    }
  }

  private changeDirection(): void {
    const props = this.isLarge ? UFO.LARGE_UFO : UFO.SMALL_UFO;
    
    // Maintain horizontal movement but vary vertical component
    const horizontalDirection = this.velocity.x > 0 ? 1 : -1;
    const verticalVariation = (Math.random() - 0.5) * 0.5; // -0.25 to 0.25
    
    this.velocity = {
      x: horizontalDirection * props.speed * (0.8 + Math.random() * 0.4), // 80-120% of base speed
      y: props.speed * verticalVariation
    };
  }

  public setTarget(targetPosition: Vector2): void {
    this.target = targetPosition;
  }

  public tryShoot(): Bullet | null {
    if (this.shootTimer < this.shootInterval) return null;

    this.shootTimer = 0;
    const props = this.isLarge ? UFO.LARGE_UFO : UFO.SMALL_UFO;

    let targetAngle: number;

    if (this.target && Math.random() < props.accuracy) {
      // Aim at player (with some accuracy)
      const dx = this.target.x - this.position.x;
      const dy = this.target.y - this.position.y;
      targetAngle = Math.atan2(dy, dx);
      
      // Add some inaccuracy
      const inaccuracy = (1 - props.accuracy) * (Math.random() - 0.5) * Math.PI / 2;
      targetAngle += inaccuracy;
    } else {
      // Random shot
      targetAngle = Math.random() * Math.PI * 2;
    }

    const bulletSpeed = 300;
    const bulletVelX = Math.cos(targetAngle) * bulletSpeed;
    const bulletVelY = Math.sin(targetAngle) * bulletSpeed;

    this.audioManager.playShoot();

    return new Bullet(
      `ufo_bullet_${Date.now()}`,
      this.position.x,
      this.position.y,
      bulletVelX,
      bulletVelY,
      true, // Enemy bullet
      this.renderManager,
      this.audioManager,
      this.gameWidth,
      this.gameHeight
    );
  }

  public getPointValue(): number {
    return this.isLarge ? UFO.LARGE_UFO.points : UFO.SMALL_UFO.points;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    const centerX = this.position.x;
    const centerY = this.position.y;

    if (this.isLarge) {
      this.renderLargeUFO(centerX, centerY);
    } else {
      this.renderSmallUFO(centerX, centerY);
    }
  }

  private renderLargeUFO(centerX: number, centerY: number): void {
    // Large UFO - more detailed design
    const radius = this.radius;
    
    // Main body (ellipse)
    this.renderManager.drawEllipse(centerX, centerY, radius * 1.2, radius * 0.8, '#FFFFFF', false);
    
    // Top dome
    this.renderManager.drawEllipse(centerX, centerY - radius * 0.3, radius * 0.8, radius * 0.5, '#FFFFFF', false);
    
    // Bottom section
    this.renderManager.drawLine(
      centerX - radius * 1.2, centerY + radius * 0.3,
      centerX + radius * 1.2, centerY + radius * 0.3,
      '#FFFFFF', 1
    );
    
    // Details/ports
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      const portX = centerX + Math.cos(angle) * radius * 0.9;
      const portY = centerY + Math.sin(angle) * radius * 0.5;
      this.renderManager.fillRect(portX - 1, portY - 1, 2, 2, '#FFFFFF');
    }
  }

  private renderSmallUFO(centerX: number, centerY: number): void {
    // Small UFO - simpler design
    const radius = this.radius;
    
    // Main body
    this.renderManager.drawEllipse(centerX, centerY, radius * 1.1, radius * 0.7, '#FFFFFF', false);
    
    // Top dome
    this.renderManager.drawEllipse(centerX, centerY - radius * 0.2, radius * 0.6, radius * 0.4, '#FFFFFF', false);
    
    // Simple details
    for (let i = 0; i < 3; i++) {
      const angle = (i / 3) * Math.PI * 2;
      const portX = centerX + Math.cos(angle) * radius * 0.8;
      const portY = centerY + Math.sin(angle) * radius * 0.4;
      this.renderManager.fillRect(portX - 1, portY - 1, 1, 1, '#FFFFFF');
    }
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x - this.radius,
      y: this.position.y - this.radius,
      width: this.radius * 2,
      height: this.radius * 2
    };
  }

  public destroy(): void {
    this.active = false;
  }
}